import { useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, CATEGORIES, TYPE_LABEL } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const Sel = ({ value, onChange, options, testid }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-input bg-white h-10 px-3 text-sm mt-1">
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export const ArticleForm = ({ article, sponsors, onClose, onSaved }) => {
  const [f, setF] = useState({
    title: "", excerpt: "", body: "", type: "analysis", category: CATEGORIES[0], access: "free", author: "Africa Brew Brief",
    author_role: "", image: "", read_time: 5, featured: false, sponsor_id: "", takeaways: [], ...article,
  });
  const [ai, setAi] = useState(!article.id);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd);
      setF((prev) => ({ ...prev, image: data.url }));
      toast.success("Cover image uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    setUploading(false);
    e.target.value = "";
  };

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = { ...f, sponsor_id: f.sponsor_id || null, read_time: Number(f.read_time) || 5, generate_ai: ai };
    try {
      if (article.id) await api.put(`/admin/articles/${article.id}`, payload); else await api.post("/admin/articles", payload);
      toast.success(article.id ? "Article updated" : "Article published"); onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="article-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{article.id ? "Edit article" : "Publish new article"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div><Label>Headline</Label><Input data-testid="article-form-title" required value={f.title} onChange={set("title")} className="rounded-none mt-1" /></div>
          <div><Label>Standfirst / excerpt</Label><Textarea data-testid="article-form-excerpt" rows={2} value={f.excerpt} onChange={set("excerpt")} className="rounded-none mt-1" /></div>
          <div><Label>Body (blank line between paragraphs, "## " for subheads)</Label><Textarea data-testid="article-form-body" required rows={10} value={f.body} onChange={set("body")} className="rounded-none mt-1 font-mono-d text-xs" /></div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Format</Label><Sel testid="article-form-type" value={f.type} onChange={set("type")} options={Object.entries(TYPE_LABEL).filter(([k]) => k !== "report")} /></div>
            <div><Label>Sector</Label><Sel testid="article-form-category" value={f.category} onChange={set("category")} options={CATEGORIES.map((c) => [c, c])} /></div>
            <div><Label>Access</Label><Sel testid="article-form-access" value={f.access} onChange={set("access")} options={[["free", "Free"], ["pro", "Pro"], ["institutional", "Institutional"]]} /></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Author</Label><Input data-testid="article-form-author" value={f.author} onChange={set("author")} className="rounded-none mt-1" /></div>
            <div><Label>Author role</Label><Input value={f.author_role} onChange={set("author_role")} className="rounded-none mt-1" /></div>
            <div><Label>Read time (min)</Label><Input type="number" value={f.read_time} onChange={set("read_time")} className="rounded-none mt-1" /></div>
          </div>
          <div><Label>Cover image</Label>
            <div className="flex gap-2 mt-1">
              <Input data-testid="article-form-image" placeholder="Paste an image link or upload" value={f.image} onChange={set("image")} className="rounded-none" />
              <label className="shrink-0 cursor-pointer border border-[#0A0A0A] px-4 flex items-center gap-2 text-sm hover:bg-[#0A0A0A] hover:text-white transition-colors">
                <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
                <input data-testid="article-form-image-upload" type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={upload} disabled={uploading} />
              </label>
            </div>
            {f.image && <img data-testid="article-form-image-preview" src={f.image} alt="Cover preview" className="mt-3 h-32 w-auto object-cover border border-[#E7E7E7]" />}
          </div>
          <div><Label>Sponsor (revenue placement)</Label><Sel testid="article-form-sponsor" value={f.sponsor_id || ""} onChange={set("sponsor_id")} options={[["", "No sponsor"], ...sponsors.map((s) => [s.id, `${s.name} — ${s.tier_label}`])]} /></div>
          <div className="flex flex-wrap gap-8 pt-2">
            <label className="flex items-center gap-3 text-sm"><Switch data-testid="article-form-featured" checked={f.featured} onCheckedChange={set("featured")} /> Lead story on homepage</label>
            <label className="flex items-center gap-3 text-sm"><Switch data-testid="article-form-ai" checked={ai} onCheckedChange={setAi} /> Generate AI key takeaways on save</label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="border border-[#E7E7E7] px-5 py-2.5 text-sm">Cancel</button>
            <button data-testid="article-form-submit" disabled={busy} className="bg-[#E5332A] text-white px-6 py-2.5 text-sm">{busy ? (ai ? "Publishing & summarising…" : "Saving…") : article.id ? "Save changes" : "Publish"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
